// Modules
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { FormattedMessage, useIntl } from "react-intl";
import isEqual from "lodash.isequal";
// Material-UI
import {
    Autocomplete,
    Button,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Stack,
    TextField,
    Typography,
    Switch
} from "@mui/material";
import { Group } from "@mui/icons-material";
// Actions
import { getPatientsSWR } from "@/actions/shared/patientActions";
// Components
import InitializeLocation from "../../../AppLayout/InitializeLocation";
// Helpers
import { UPDATE_GROUPED_SESSION } from "../../../../helpers/constants";
// Styles
import { gridSpacing } from "../../../../styles/constants";
import { User } from "@/lib/types/types";

const MAX_ALLOWED_GUESTS = 2;

const getInitialValues = ({ updateEvent, hasProps, bookEventFormProps, patients }) => {
    const { title, start, guests = [] } = updateEvent || {};
    const { patient = [] } = bookEventFormProps || {};

    const startDate = start ? new Date(start) : undefined;
    const endDate = startDate ? new Date(startDate) : undefined;
    endDate?.setHours(endDate?.getHours() + 1);

    const guestsWithChildName = guests.map(guest => ({
        ...guest,
        childName: patients.find(patient => patient.email === guest.email)?.child?.name
    }));

    const initialValues = {
        title: title || "",
        guests:
            guests.length > 0
                ? guestsWithChildName
                : hasProps
                ? patient.map(({ name, email }) => ({ name, email }))
                : []
    };

    return initialValues;
};

const validationSchema = Yup.object().shape({
    title: Yup.string().max(255),
    guests: Yup.array().min(1, <FormattedMessage id="error-required-field" />)
});

const buildTherapyTitle = ({ intl, therapist, patient, isGroupedSession }) => {
    const therapyLabel = intl.formatMessage({ id: "therapy" });
    const guestName = patient && "childName" in patient ? patient.childName : patient?.name;

    const sessionType = !isGroupedSession && guestName;

    // Build the title, only including sessionType if it's truthy
    return `${therapyLabel} ${therapist?.name}${sessionType ? ` - ${sessionType}` : ""}`;
};

type EditGroupedSessionFormProps = {
    locale: Locale;
    therapist: User;
    updateEvent: any;
    onCancel: () => void;
    handleUpdate: (params: {
        eventId: string;
        userId: string;
        params: { operation: string };
        update: { data: any; eventUpdateLog: { date: Date; setRecurring: boolean } };
    }) => void;
    bookEventFormProps: any;
};
const EditGroupedSessionForm = ({
    locale,
    therapist,
    updateEvent,
    onCancel,
    handleUpdate,
    bookEventFormProps
}: EditGroupedSessionFormProps) => {
    const intl = useIntl();

    const [isGroupedSession, setIsGroupedSession] = useState(updateEvent.isGroupedSession || false);

    const { guests = [] } = updateEvent || {};
    const initialMaxParticipants = guests.length || (isGroupedSession ? MAX_ALLOWED_GUESTS : 1);
    const [maxParticipants, setMaxParticipants] = useState(() => initialMaxParticipants);

    const hasProps = Object.keys(bookEventFormProps).length > 0;
    const patients = hasProps
        ? bookEventFormProps.patient.map(({ name, email }) => ({ name, email }))
        : getPatientsSWR({ displaySpinner: false });

    const handleIsGroupedSessionCheck = event => {
        const isChecked = event.target.checked;
        setIsGroupedSession(isChecked);

        if (isGroupedSession) setFieldValue("guests", []);

        // Set max participants state to 0, to allow user to select
        setMaxParticipants(0);
    };

    const formik = useFormik({
        initialValues: getInitialValues({
            updateEvent,
            hasProps,
            bookEventFormProps,
            patients
        }),
        validationSchema,
        onSubmit: async values => {
            const operation = UPDATE_GROUPED_SESSION;
            const {
                guests: updatedGuests,
                isGroupedSession: updatedIsGroupedSession,
                start: eventStartDate
            } = updateEvent || {};
            const { guests = [] } = values || {};

            const hasChanges = !isEqual(updatedGuests, guests) || updatedIsGroupedSession !== isGroupedSession;
            if (!hasChanges) {
                onCancel && onCancel();
                formik.resetForm();
                return;
            }

            const session = {
                ...updateEvent,
                title: buildTherapyTitle({
                    intl,
                    therapist,
                    patient: guests && guests.length > 0 && guests[0],
                    isGroupedSession
                }),
                ...(isGroupedSession && { isGroupedSession: true }),
                guests: guests?.map(guest => ({ name: guest.name, email: guest.email }))
            };

            const eventUpdateLog = {
                date: eventStartDate,
                setRecurring: false
            };

            if (!isGroupedSession) Reflect.deleteProperty(session, "isGroupedSession");

            handleUpdate({
                eventId: updateEvent?.id,
                userId: updateEvent?.user._id,
                params: { operation },
                update: { data: session, eventUpdateLog }
            });
        }
    });

    useEffect(() => {
        return () => {
            formik?.resetForm();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { values, errors, touched, setFieldValue } = formik;

    const handleSubmit = e => {
        e.preventDefault();

        formik.handleSubmit(e);
    };

    return (
        <InitializeLocation adapter="date" locale={locale}>
            <form noValidate onSubmit={handleSubmit}>
                <DialogTitle>
                    <FormattedMessage id="edit-session" />
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <TextField
                                disabled
                                fullWidth
                                label={<FormattedMessage id="title" />}
                                value={buildTherapyTitle({
                                    intl,
                                    therapist,
                                    // eslint-disable-next-line no-extra-boolean-cast
                                    patient: hasProps
                                        ? bookEventFormProps?.patient[0]
                                        : values.guests &&
                                          values.guests.length > 0 && {
                                              ...values.guests[0],
                                              childName: patients.find(
                                                  patient => patient.email === values.guests[0].email
                                              )?.child?.name
                                          },
                                    isGroupedSession
                                })}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={12} sx={{ minWidth: "min-content" }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isGroupedSession}
                                                onChange={handleIsGroupedSessionCheck}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Stack
                                                spacing={1}
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={{ width: "100%" }}
                                            >
                                                <Group color="primary" />
                                                <Stack spacing={0} sx={{ width: "100%" }}>
                                                    <Typography variant="subtitle1" color="primary">
                                                        <FormattedMessage id="is-grouped-session" />
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                disabled
                                fullWidth
                                label={<FormattedMessage id="therapist" />}
                                value={`${therapist.name} - ${therapist.email}`}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                id="guests"
                                multiple
                                disabled={hasProps}
                                limitTags={2}
                                filterOptions={(options, state) => {
                                    const maxGuests = isGroupedSession ? MAX_ALLOWED_GUESTS : 1;
                                    const searchValue = state.inputValue.toLowerCase();

                                    return options.filter(option => {
                                        const { name = "", email = "" } = option || {};

                                        // Check if the search input matches either the name or the email
                                        const matchesSearch = [name, email].some(field =>
                                            (field ?? "").toLowerCase().includes(searchValue)
                                        );

                                        // Ensure that guests can still be added
                                        const canAddMoreGuests = maxParticipants < maxGuests;
                                        return matchesSearch && canAddMoreGuests;
                                    });
                                }}
                                filterSelectedOptions
                                getOptionLabel={option =>
                                    `${option.childName} (${intl.formatMessage({ id: "parent" })}: ${option.name}) - ${
                                        option.email
                                    }`
                                }
                                isOptionEqualToValue={(option, value) => option?.email === value?.email}
                                noOptionsText={<FormattedMessage id="no-options" />}
                                options={[...(patients || [])].map(option => ({
                                    name: option.name,
                                    email: option.email,
                                    childName: option?.child?.name
                                }))}
                                onChange={(event, value) => {
                                    const maxGuests = isGroupedSession ? MAX_ALLOWED_GUESTS : 1;

                                    if (value.length <= maxGuests) {
                                        setMaxParticipants(value.length);
                                        setFieldValue("guests", value);
                                    }
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        id={"guests"}
                                        label={<FormattedMessage id="guests" />}
                                        name={"guests"}
                                        error={touched.guests && Boolean(errors.guests)}
                                        helperText={touched.guests && errors.guests}
                                    />
                                )}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip
                                            key={option.email}
                                            label={`${option.childName} (${intl.formatMessage({ id: "parent" })}: ${
                                                option.name
                                            }) - ${option.email}`}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                                value={values.guests}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Grid container justifyContent="flex-end" alignItems="center">
                        <Grid item>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button type="button" variant="outlined" onClick={onCancel}>
                                    <FormattedMessage id="cancel" />
                                </Button>
                                <Button type="submit" variant="contained" data-testid="save-event">
                                    <FormattedMessage id="save" />
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogActions>
            </form>
        </InitializeLocation>
    );
};

export default EditGroupedSessionForm;
